import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  ChartBarIcon,
  UsersIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  ClockIcon,
  GlobeAltIcon,
  ArrowTrendingUpIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

const AnalyticsOverview = ({ days = 30 }) => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState(days);

  const fetchAnalytics = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`/api/analytics/stats?days=${selectedPeriod}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch analytics');
      }

      const data = await response.json();
      if (data.status === 'success') {
        setStats(data.data);
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [token, selectedPeriod]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        <p className="font-bold">Error</p>
        <p>{error}</p>
      </div>
    );
  }

  if (!stats) return null;

  const StatCard = ({ icon: Icon, title, value, subtitle, color = 'blue' }) => {
    const colorClasses = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      purple: 'bg-purple-500',
      orange: 'bg-orange-500',
      teal: 'bg-teal-500',
      indigo: 'bg-indigo-500'
    };

    return (
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="p-5">
          <div className="flex items-center">
            <div className={`flex-shrink-0 ${colorClasses[color]} rounded-md p-3`}>
              <Icon className="h-6 w-6 text-white" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
                <dd className="flex items-baseline">
                  <div className="text-2xl font-semibold text-gray-900">{value}</div>
                  {subtitle && (
                    <div className="ml-2 text-sm text-gray-500">{subtitle}</div>
                  )}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Website Analytics</h2>
        <div className="flex space-x-2">
          {[7, 30, 90].map(period => (
            <button
              key={period}
              onClick={() => setSelectedPeriod(period)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedPeriod === period
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Last {period} days
            </button>
          ))}
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={EyeIcon}
          title="Total Page Views"
          value={stats.totalViews?.toLocaleString()}
          color="blue"
        />
        <StatCard
          icon={UsersIcon}
          title="Unique Visitors"
          value={stats.uniqueVisitors?.toLocaleString()}
          color="green"
        />
        <StatCard
          icon={DevicePhoneMobileIcon}
          title="Mobile Traffic"
          value={stats.mobileViews?.toLocaleString()}
          subtitle={`${stats.mobilePercentage}%`}
          color="purple"
        />
        <StatCard
          icon={ClockIcon}
          title="Avg Response Time"
          value={stats.avgResponseTime ? `${stats.avgResponseTime}ms` : 'N/A'}
          color="orange"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Pages */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <ChartBarIcon className="h-5 w-5 mr-2 text-blue-500" />
            Top Pages
          </h3>
          <div className="space-y-3">
            {stats.topPages?.slice(0, 10).map((page, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate" title={page.url}>
                    {page.url}
                  </p>
                </div>
                <div className="ml-4 flex items-center">
                  <span className="text-sm text-gray-500">{page.views} views</span>
                  <div className="ml-2 w-24 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{
                        width: `${(page.views / stats.topPages[0].views) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Browser Stats */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <GlobeAltIcon className="h-5 w-5 mr-2 text-green-500" />
            Browsers
          </h3>
          <div className="space-y-3">
            {stats.browserStats?.map((browser, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">{browser.browser}</span>
                <div className="flex items-center">
                  <span className="text-sm text-gray-500 mr-2">{browser.count}</span>
                  <div className="w-20 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{
                        width: `${(browser.count / stats.browserStats[0].count) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* OS Stats */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <ComputerDesktopIcon className="h-5 w-5 mr-2 text-purple-500" />
            Operating Systems
          </h3>
          <div className="space-y-3">
            {stats.osStats?.map((os, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">{os.os}</span>
                <div className="flex items-center">
                  <span className="text-sm text-gray-500 mr-2">{os.count}</span>
                  <div className="w-20 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-purple-600 h-2 rounded-full"
                      style={{
                        width: `${(os.count / stats.osStats[0].count) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Views by Day Chart */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <ArrowTrendingUpIcon className="h-5 w-5 mr-2 text-teal-500" />
            Daily Traffic Trend
          </h3>
          <div className="space-y-2">
            {stats.viewsByDay?.slice(-14).map((day, index) => {
              const maxViews = Math.max(...stats.viewsByDay.map(d => d.views));
              return (
                <div key={index} className="flex items-center">
                  <span className="text-xs text-gray-500 w-20">{new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  <div className="flex-1 ml-2">
                    <div className="bg-gray-200 rounded-full h-4">
                      <div
                        className="bg-teal-500 h-4 rounded-full flex items-center justify-end pr-2"
                        style={{ width: `${(day.views / maxViews) * 100}%` }}
                      >
                        <span className="text-xs text-white font-medium">{day.views}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Mobile vs Desktop */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Device Breakdown</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <DevicePhoneMobileIcon className="h-12 w-12 mx-auto text-purple-600 mb-2" />
            <p className="text-2xl font-bold text-purple-900">{stats.mobileViews?.toLocaleString()}</p>
            <p className="text-sm text-purple-700">Mobile ({stats.mobilePercentage}%)</p>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <ComputerDesktopIcon className="h-12 w-12 mx-auto text-blue-600 mb-2" />
            <p className="text-2xl font-bold text-blue-900">{stats.desktopViews?.toLocaleString()}</p>
            <p className="text-sm text-blue-700">Desktop ({(100 - parseFloat(stats.mobilePercentage)).toFixed(2)}%)</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsOverview;

